import { createTRPCClient, httpBatchLink } from "@trpc/client";

import type { AppRouter } from "@command-control/server/router";

let commandControlAuthToken: string | null = null;
const COMMAND_CONTROL_REQUEST_TIMEOUT_MS = 15_000;

export function setCommandControlAuthToken(token: string | null): void {
  commandControlAuthToken = token;
}

function defaultApiUrl(): string {
  if (typeof window === "undefined") {
    return "http://localhost:8787/trpc";
  }

  return "/trpc";
}

export function createCommandControlClient(apiUrl = defaultApiUrl()) {
  return createTRPCClient<AppRouter>({
    links: [
      httpBatchLink({
        fetch: (url, options) => {
          const requestOptions = { ...(options ?? {}) };
          const abortController = new AbortController();
          const timeout = setTimeout(() => abortController.abort(), COMMAND_CONTROL_REQUEST_TIMEOUT_MS);

          if ("signal" in requestOptions && requestOptions.signal === undefined) {
            delete requestOptions.signal;
          }

          const upstreamSignal = "signal" in requestOptions ? requestOptions.signal : undefined;
          if (upstreamSignal instanceof AbortSignal) {
            if (upstreamSignal.aborted) {
              abortController.abort();
            } else {
              upstreamSignal.addEventListener("abort", () => abortController.abort(), { once: true });
            }
          }

          return fetch(url, {
            ...requestOptions,
            credentials: "include",
            signal: abortController.signal,
          } as Parameters<typeof fetch>[1]).finally(() => clearTimeout(timeout));
        },
        headers: () => (commandControlAuthToken ? { authorization: `Bearer ${commandControlAuthToken}` } : {}),
        url: apiUrl,
      }),
    ],
  });
}

export type CommandControlClient = ReturnType<typeof createCommandControlClient>;
export type { AppRouter };
